import { ClipboardModule } from '@angular/cdk/clipboard'
import { CommonModule } from '@angular/common'
import {
  AfterViewChecked,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

import { Store } from '@ngrx/store'
import { some as _some } from 'lodash'
import { CarouselModule } from 'primeng/carousel'
import { DialogModule } from 'primeng/dialog'
import { ImageModule } from 'primeng/image'
import { PanelModule } from 'primeng/panel'
import { PopoverModule } from 'primeng/popover'
import { TooltipModule } from 'primeng/tooltip'

import { filter } from 'rxjs'


import {
  FlxButtonModule,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalModule,
} from '@flowx/angular-ui-toolkit'
import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { AnalystSummaryComponent } from '@flowx/designer/modules/new-ai/agents/analyst/components/ai-analyst-summary/ai-analyst-summary.component'
import { AiCoordinatorActions } from '@flowx/designer/modules/new-ai/agents/coordinator/store/coordinator.actions'
import { AiDataModelModifyModalComponent } from '@flowx/designer/modules/new-ai/agents/designer/components/ai-data-model-modify-modal/ai-data-model-modify-modal.component'
import { AiDesignerActions } from '@flowx/designer/modules/new-ai/agents/designer/store/designer.actions'
import { DocumentsService } from '@flowx/designer/modules/new-ai/agents/documents/api/documents.service'
import {
  GeneratingItem,
  SuggestedPrompt,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { FileExtension, FileMimeType } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import {
  AiAgent,
  AiAnalystContext,
  ContextDocument,
  FeedbackType,
  Message,
  MessageType,
  imageFileTypes,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { resetInputFileValueFromEvent } from '@flowx/designer/modules/new-ai/common/ai.utils'
import { AiIconsModule } from '@flowx/designer/modules/new-ai/common/icons/ai-icons.module'
import { HighlightBracketedPipe } from '@flowx/designer/modules/new-ai/common/pipes/flx-ai-markdown.pipe'
import { FlxAiMarkdownPipe } from '@flowx/designer/modules/new-ai/common/pipes/flx-ai-markdown.pipe'
// eslint-disable-next-line import/no-cycle
import { CommandPaletteService } from '@flowx/designer/modules/new-ai/modules/command-palette/components/command-palette/command-palette.service'
import { AiActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AppStores } from '@flowx/designer/shared/effector/app/app.stores'
import { convertBlobToBase64 } from '@flowx/designer/shared/utils/file.utils'

import { AiSelectors } from '../../../../store/ai.selectors'

const MAX_GENERATORS = 4

@Component({
  selector: 'flx-ai-chat-bubble',
  templateUrl: './ai-chat-bubble.component.html',
  styleUrls: ['./ai-chat-bubble.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ClipboardModule,
    ImageModule,
    TooltipModule,
    DialogModule,
    PopoverModule,
    FlxAiMarkdownPipe,
    HighlightBracketedPipe,
    AiIconsModule,
    AnalystSummaryComponent,
    FlxButtonModule,
    FlxFormControlModule,
    FlxIconModule,
    FlxModalModule,
    PanelModule,
    CarouselModule,
  ],
})
export class AiChatBubbleComponent implements AfterViewChecked {
  #store = inject(Store)
  #destroyRef = inject(DestroyRef)
  #flxModalService = inject(FlxModalService)
  #commandPaletteService = inject(CommandPaletteService)
  #documentsService = inject(DocumentsService)
  #sanitizer = inject(DomSanitizer)
  // holds resolved images for the current generators set
  private _resolvedGeneratorImages = signal<any[]>([])
  private _lastGeneratorsImagesKey: string | null = null

  @ViewChild('streamContainer', { static: false }) containerRef!: ElementRef<HTMLDivElement>
  @ViewChild('thinkingContainer', { static: false })
  thinkingContainerRef: ElementRef<HTMLDivElement>

  message = input.required<Message>()
  previousMessage = signal<Message | null>(null)
  loading = input<boolean>(false)

  hasMessageActions = computed(
    () =>
      this.message().actions &&
      !this.message().actions?.yesNo &&
      _some(Object.values(this.message().actions || {}))
  )

  // UI Flow page list for switching between generated pages
  uiFlowPages = computed(() => (this.message().context as any)?.uiFlowPages || [])
  selectedPageIndex = this.#store.selectSignal(AiSelectors.selectedUiFlowPageIndex)
  hasDraft = this.#store.selectSignal(AiSelectors.hasDraft)

  onSelectPage(pageIndex: number): void {
    this.#store.dispatch(AiActions.selectUiFlowPage({ pageIndex }))
  }
  isMessageLast = computed(() => {
    const lastMessage = this.#store.selectSignal(AiSelectors.lastMessage)

    return this.message() === lastMessage()
  })

  streamingAiResponse = this.#store.selectSignal(AiSelectors.streamingAiResponse)

  showImageUploadAction = computed(() => this.message().actions?.imageUpload)
  showBpmnUploadAction = computed(() => this.message().actions?.bpmnUpload)
  allowedImageFileTypes = this.#store.selectSignal(AiSelectors.allowedImageFileTypes)
  allowedBpmnFileTypes = this.#store.selectSignal(AiSelectors.allowedBpmnFileTypes)
  isMultipleImageUploadAllowed = this.#store.selectSignal(AiSelectors.isMultipleImageUploadAllowed)
  pageAgent = this.#store.selectSignal(AiSelectors.pageAgent)

  showProcessUploadAction = computed(() => this.message().actions?.processUpload)
  allowedProcessFileTypes = this.#store.selectSignal(AiSelectors.allowedProcessFileTypes)
  isMultipleProcessUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleProcessUploadAllowed
  )

  showFromDocumentsAction = computed(() => this.message().actions?.checkDocumentContext)
  documents = computed(() => this.message().documents?.files || [])
  agents = computed(() => this.message().agents || [])
  showAgents = computed(() => this.agents().length > 0)
  showDocuments = computed(() => !!this.message().documents)
  showThinking = computed(
    () =>
      !!this.message().thinking && this.message().thinking?.trim() !== '' && this.isMessageLast()
  )

  showDocumentUploadAction = computed(() => this.message().actions?.documentUpload)
  allowedDocumentFileTypes = this.#store.selectSignal(AiSelectors.allowedDocumentFileTypes)
  isMultipleDocumentUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleDocumentUploadAllowed
  )

  isDocumentDisabled = computed(
    () =>
      this.#store.selectSignal(AiSelectors.hasSelectedDocument)() ||
      this.#store.selectSignal(AiSelectors.pendingAiResponse)()
  )
  isUploadDisabled = this.#store.selectSignal(AiSelectors.isUploadDisabled)

  isGenerateFromDocumentContextDisabled = computed(
    () =>
      this.message().generators?.documentId !==
        this.#store.selectSignal(AiSelectors.selectedDocument)()?.documentId ||
      this.#store.selectSignal(AiSelectors.isGenerateFromDocumentContextDisabled)()
  )

  isUserMessage = computed(() => this.message().type === MessageType.UserMsg)
  isErrorMessage = computed(() => this.message().type === MessageType.ErrorMsg)
  showStartNewSessionAction = computed(() => this.message().actions?.startNewSession)
  showYesNoAction = computed(() => this.message().actions?.yesNo)
  interruptOptions = computed(() => this.message().actions?.interruptOptions)
  userInitials = AppStores.userProfile$.getState().initials
  isManualExpandedGenerators = signal(false)
  isExpandedGenerators = computed(
    () => this.isManualExpandedGenerators() || this.collapsedGeneratorsLimitReached()
  )
  collapsedGeneratorsLimitReached = computed(() => {
    const { generators } = this.message()
    const generatorsNo = generators?.items?.length
    return generatorsNo <= MAX_GENERATORS
  })

  generators = computed(() => {
    const { generators } = this.message()
    const isExpandedGenerators = this.isExpandedGenerators()
    const items = generators?.items?.map((item) => ({ ...item, expandedDescription: false })) ?? []
    let label = ''
    let action = ''
    switch (generators?.agent) {
      case AiAgent.ANALYST:
        label = 'processes'
        action = 'generate'
        break
      case AiAgent.DESIGNER:
        label = 'designs'
        action = 'generate'
        break
      case AiAgent.DEVELOPER:
        label = 'business rules'
        action = 'generate'
        break
      case AiAgent.AUDITOR:
        label = 'checks'
        action = 'use'
    }

    const imagesKey =
      generators?.images?.map((i: any) => `${i.resource_id}::${i.storagePath}`).join('|') || ''
    if (imagesKey && imagesKey !== this._lastGeneratorsImagesKey) {
      this._lastGeneratorsImagesKey = imagesKey
      // kick off async load (don't await inside computed)
      void this.loadGeneratorImages(generators.images)
    }

    return {
      summary: generators?.summary,
      description: generators?.description,
      label,
      action,
      items: items.slice(0, isExpandedGenerators ? undefined : MAX_GENERATORS),
      count: items.length,
      images: this._resolvedGeneratorImages(),
    }
  })

  isCopyMessageAllowed = computed(
    () => !this.loading() && this.message().message && this.message().message?.trim() !== ''
  )
  copiedTextToChat = signal(false)
  copyTextToChatTooltip = computed(() =>
    this.isCopyMessageAllowed() ? (this.copiedTextToChat() ? 'Copied!' : 'Copy to chat') : null
  )
  copyTextToChatIcon = computed(() => (!this.copiedTextToChat() ? 'copy-prompt' : 'circle-check'))

  changes = computed(() => {
    const message = this.message()
    const context = message.context as AiAnalystContext

    return { added: context?.changes?.added, removed: context?.changes?.removed }
  })

  warnings = computed(() => {
    const message = this.message()
    const context = message.context as AiAnalystContext

    return context?.answer?.warnings || []
  })

  showFeedback = computed(() => {
    const { type } = this.message()
    const isLoading = this.loading()

    return !isLoading && type === MessageType.AiMsg
  })
  isFeedbackType = computed(() => {
    const { feedback } = this.message()

    return {
      [FeedbackType.POSITIVE]: feedback === FeedbackType.POSITIVE,
      [FeedbackType.NEGATIVE]: feedback === FeedbackType.NEGATIVE,
    }
  })

  canSendFeedback = computed(() => !this.message().feedback)

  images = computed(() => {
    const { files } = this.message()

    return (
      files
        ?.filter((file) => imageFileTypes.includes(file.type as FileMimeType))
        .map((image) => ({
          name: image.name,
          src$: convertBlobToBase64(image),
        })) ?? []
    )
  })

  bpmnFiles = computed(() => {
    const { files } = this.message()

    return (
      files
        ?.filter((file) => file.name.endsWith(FileExtension.BPMN))
        .map((file) => ({
          name: file.name,
          src$: convertBlobToBase64(file),
        })) ?? []
    )
  })

  dataModelChanges = computed(() => this.message()?.dataModel)
  dataModelChangesSummary = computed(() => {
    const dataModelChanges = this.dataModelChanges()

    return {
      noNew: dataModelChanges?.new?.filter(({ inactive }) => !inactive).length,
      noExisting: dataModelChanges?.existing?.filter(({ inactive }) => !inactive).length,
    }
  })
  hasExistingDataModelChanges = computed(() => !!this.dataModelChanges()?.existing?.length)
  hasNewDataModelChanges = computed(() => !!this.dataModelChanges()?.new?.length)
  hasDataModelChanges = computed(
    () => this.hasExistingDataModelChanges() || this.hasNewDataModelChanges()
  )

  sendFeedback = output<FeedbackType>()
  previewHtmlVisible = signal(false)
  FeedbackType = FeedbackType

  showPreviewHtml(): void {
    this.previewHtmlVisible.set(true)
  }

  closePreviewHtml(): void {
    this.previewHtmlVisible.set(false)
  }

  getSafeHtml(): SafeHtml {
    const rawHtml = this.message().html_preview
    return this.#sanitizer.bypassSecurityTrustHtml(rawHtml)
  }

  isMultiLineOverflows(el: HTMLElement): boolean {
    return el.scrollHeight > el.clientHeight
  }

  ngAfterViewChecked(): void {
    const currentMessage = this.message()
    if (
      this.streamingAiResponse() &&
      this.isMessageLast() &&
      !this.isErrorMessage() &&
      !this.isUserMessage() &&
      currentMessage !== this.previousMessage()
    ) {
      this.insertCursorInline()
      this.previousMessage.set(currentMessage)
    } else {
      this.removeCursor()
    }

    // Auto-scroll thinking container to bottom during streaming
    if (this.thinkingContainerRef?.nativeElement) {
      const el = this.thinkingContainerRef.nativeElement
      el.scrollTop = el.scrollHeight
    }
  }

  checkContextDocuments(): void {
    this.#store.dispatch(AiActions.fromDocumentsClick())
  }

  showAllGenerators(): void {
    this.isManualExpandedGenerators.set(true)
  }

  promptSelected(prompt: SuggestedPrompt): void {
    this.#store.dispatch(
      AiActions.typeAreaSubmit({
        text: prompt?.action + ' -> ' + prompt?.description,
        agent: prompt?.handled_by,
      })
    )
  }

  yesAction(): void {
    this.#store.dispatch(AiActions.yesAction())
  }

  noAction(): void {
    this.#store.dispatch(AiActions.noAction())
  }

  selectInterruptOption(option: { label: string; value: string }): void {
    this.#store.dispatch(AiActions.selectInterruptOption({ option }))
  }

  objectSelected(object: string): void {
    this.#commandPaletteService.navigateToObject(object)
  }

  implementImage(image: any): void {
    const agent = AiAgent.DESIGNER

    // normalize input to a File instance
    let files: File[] = []

    // If it's already a File, use it directly
    if (image instanceof File) {
      files = [image]
    } else {
      // image may be an object like { name, src } or a data URL string
      const dataUrl =
        typeof image === 'string'
          ? image
          : (image?.src ?? (image && (image as any).toString && String(image)))

      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
        const name = (image && image.name) || `image_${Date.now()}.jpg`
        files = [this.dataUrlToFile(dataUrl, name)]
      } else if (image && (image as any).blob instanceof Blob) {
        // handle objects that already carry a blob
        const blob = (image as any).blob as Blob
        const name = (image && image.name) || `image_${Date.now()}`
        files = [new File([blob], name, { type: blob.type || 'application/octet-stream' })]
      } else {
        // fallback: try to coerce whatever was passed to a File-like object
        try {
          files = [image as File]
        } catch (err) {
          console.error('implementImage: could not convert image to File', err, image)
          files = []
        }
      }
    }

    if (files.length) {
      this.#store.dispatch(AiActions.generateFromFiles({ files, prompt: '', agent }))
    } else {
      console.warn('No files to generate from for implementImage', image)
    }
  }

  showDataModelModifyModal(): void {
    const data = { dataModel: this.message()?.dataModel }
    const modal = this.#flxModalService.show(
      AiDataModelModifyModalComponent,
      {
        closeOnBackdropClick: false,
        panelClass: 'maximized-modal',
      },
      data ?? {}
    )
    modal.data$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter(({ confirm }) => !!confirm)
      )
      .subscribe(({ updatedDataModel }) => {
        this.#store.dispatch(
          AiDesignerActions.updateDataModelOnLastMessage({
            dataModel: updatedDataModel,
          })
        )
      })
  }

  async loadGeneratorImages(images: any[] | undefined): Promise<void> {
    if (!images || !images.length) {
      this._resolvedGeneratorImages.set([])
      return
    }

    try {
      const resolved = await Promise.all(
        images
          .filter((img) => img.resource_id && img.storagePath)
          .map(async (image) => {
            const res = await this.#documentsService.getImageContent(
              image.storagePath,
              image.resource_id,
              'image/jpeg',
              this.#commandPaletteService.getApplicationId(),
              this.#commandPaletteService.getApplicationVersionId()
            )
            const src =
              res && res.content
                ? `data:${res.content_type || 'image/jpeg'};base64,${res.content}`
                : null
            return {
              name: image.name,
              type: image.type,
              src,
            }
          })
      )
      this._resolvedGeneratorImages.set(resolved)
    } catch (error) {
      console.error('loadGeneratorImages error', error)
      this._resolvedGeneratorImages.set([])
    }
  }
  getIconByAgent(agent: AiAgent): string {
    switch (agent) {
      case AiAgent.ASSISTANT:
        return 'ph ph-question-mark font-bold'
      case AiAgent.ANALYST:
        return 'ph ph-tree-structure font-bold'
      case AiAgent.DESIGNER:
        return 'ph ph-layout font-bold'
      case AiAgent.DEVELOPER:
        return 'ph ph-code font-bold'
      case AiAgent.AUDITOR:
        return 'ph ph-checks font-bold'
      case AiAgent.ARCHITECT:
        return 'ph ph-database font-bold'
      case AiAgent.INTEGRATOR:
        return 'ph ph-hard-drives font-bold'
      default:
        return 'ph ph-dots-three font-bold'
    }
  }
  getIconByType(type: string): string {
    switch (type) {
      case 'node':
        return 'ph pt-1 text-yellow-500 ph-tree-structure font-bold'
      case 'action':
        return 'ph pt-1 text-yellow-500 ph-code font-bold'
      case 'ui':
        return 'ph pt-1 text-yellow-500 ph-layout font-bold'
      case 'datamodel':
        return 'ph pt-1 text-yellow-500 ph-database font-bold'
      default:
        return 'document'
    }
  }

  copyToChatInputField(): void {
    this.#store.dispatch(AiActions.copyToChatInputField({ text: this.message().message }))
    this.copyPrompt(this.message().message)
    this.copiedTextToChat.set(true)
    setTimeout(() => this.copiedTextToChat.set(false), 2000)
  }

  generateFromDocument(generator: GeneratingItem): void {
    if (generator.type === 'screen_name') {
      this.#store.dispatch(
        AiActions.generate({
          prompt:
            'Description:' +
            generator.description +
            '; Screen Name: ' +
            generator.name +
            '; Screen value: ' +
            generator.value,
          agent: AiAgent.DESIGNER,
        })
      )
      return
    } else if (generator.type === 'business_process') {
      this.#store.dispatch(
        AiActions.generate({
          prompt:
            'Description:' +
            generator.description +
            '; Business Process Name: ' +
            generator.name +
            '; Business Process Details: ' +
            generator.value,
          agent: AiAgent.COORDINATOR,
        })
      )
      return
    }
    // this.#store.dispatch(
    //   AiActions.generateFromDocumentClick({
    //     documentContextReference: {
    //       name: generator.name,
    //       documentId: this.message().generators.documentId,
    //       type: 'custom',
    //       summary: this.generators().summary,
    //       value: generator.value,
    //       description: generator.description,
    //     },
    //   })
    // )
  }

  generatePlanFromSummary(): void {
    const generators = this.message().generators
    if (!generators?.documentId) {
      return
    }

    const appId = this.#store.selectSignal(AiSelectors.getAppId)()
    const appVersionId = this.#store.selectSignal(AiSelectors.getAppVersionId)()
    const sessionId = this.#store.selectSignal(AiSelectors.sessionId)()
    const selectedDoc = this.#store.selectSignal(AiSelectors.selectedDocument)()

    if (selectedDoc) {
      this.#store.dispatch(
        AiActions.selectExtractedDocument({ document: selectedDoc, pageAgent: this.pageAgent() })
      )
    }

    this.#store.dispatch(
      AiCoordinatorActions.generatePlan({
        prompt: 'Generate a comprehensive plan from the uploaded document',
        appId,
        appVersion: appVersionId,
        plan_type: 'application',
        current_plan: null,
        context: null,
        session_id: sessionId,
        document_id: generators.documentId,
      })
    )
  }

  submitFeedback(feedbackType: FeedbackType): void {
    if (this.canSendFeedback()) {
      this.sendFeedback.emit(feedbackType)
    }
  }

  copyPrompt(prompt: string): void {
    navigator.clipboard.writeText(prompt)
  }

  startNewSession(): void {
    this.#store.dispatch(AiActions.startNewSession())
  }

  onContextDocumentClick(document: ContextDocument): void {
    this.#store.dispatch(AiActions.documentClick({ document, pageAgent: this.pageAgent() }))
  }

  onFileSelected(event: Event): void {
    this.#store.dispatch(AiActions.fileUploaded({ event }))
    resetInputFileValueFromEvent(event)
  }

  insertCursorInline(): void {
    const container = this.containerRef?.nativeElement
    if (!container) {
      return
    }

    this.removeCursor() // prevent duplicates

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null)
    let lastTextNode: Text | null = null

    while (walker.nextNode()) {
      const node = walker.currentNode as Text
      if (node.nodeValue?.trim()) {
        lastTextNode = node
      }
    }

    if (lastTextNode) {
      const span = document.createElement('span')
      span.className = 'blinking-cursor'
      lastTextNode.parentNode?.insertBefore(span, lastTextNode.nextSibling)
    }
  }

  removeCursor(): void {
    const cursor = this.containerRef?.nativeElement.querySelector('.blinking-cursor')
    if (cursor) {
      cursor.remove()
    }
  }
  implementAgent(agent: AiAgent): void {
    this.#store.dispatch(AiActions.implementAgent({ agent }))
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const arr = dataUrl.split(',')
    const mimeMatch = arr[0].match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], fileName, { type: mime })
  }
}
